import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { requestYieldCallback, getViewParameterValue } from "@theme/utilities";

/**
 * Triggers staggered animation for media gallery items on variant change
 */
export function triggerMediaGalleryStaggeredAnimation() {
  // Find all media galleries on the page
  const mediaGalleries = document.querySelectorAll("media-gallery[data-media-gallery-block-id]");
  const mediaGalleryGrids = document.querySelectorAll("[data-media-gallery-block-id]");

  if (mediaGalleries.length === 0 && mediaGalleryGrids.length === 0) {
    return;
  }

  /**
   * @param {Element | null} mediaGallery
   * @param {Element | null} mediaGalleryGrid
   * @returns {Element[]}
   */
  function getMediaItems(mediaGallery, mediaGalleryGrid) {
    // Get items from grid layout
    const gridItems = mediaGalleryGrid ? mediaGalleryGrid.querySelectorAll(".media-gallery-item") : [];

    // Get items from carousel/slideshow layout
    const carouselItems = mediaGallery ? mediaGallery.querySelectorAll(".media-gallery-item, slideshow-slide") : [];

    // Combine both and return unique items
    /** @type {Element[]} */
    const allItems = [];
    gridItems.forEach((item) => allItems.push(item));
    carouselItems.forEach((item) => {
      if (!allItems.includes(item)) {
        allItems.push(item);
      }
    });

    return allItems;
  }

  /**
   * @param {Element | null} mediaGallery
   * @param {Element | null} mediaGalleryGrid
   */
  function triggerAnimation(mediaGallery, mediaGalleryGrid) {
    const mediaItems = getMediaItems(mediaGallery, mediaGalleryGrid);
    if (mediaItems.length === 0) {
      return;
    }

    // Remove animation class first
    mediaItems.forEach((item) => {
      item.classList.remove("media-gallery-item--animating");
    });

    // Trigger reflow
    const container = mediaGalleryGrid || mediaGallery;
    if (container instanceof HTMLElement) {
      void container.offsetHeight;
    }

    // Add animation class to trigger staggered animation
    mediaItems.forEach((item) => {
      item.classList.add("media-gallery-item--animating");
    });

    // Clean up classes after animation completes
    setTimeout(() => {
      mediaItems.forEach((item) => {
        item.classList.remove("media-gallery-item--animating");
      });
    }, 2000);
  }

  // Trigger animation for each media gallery found
  mediaGalleries.forEach((mediaGallery) => {
    const blockId = mediaGallery.getAttribute("data-media-gallery-block-id");
    const mediaGalleryGrid = document.querySelector(`[data-media-gallery-block-id="${blockId}"]`);
    triggerAnimation(mediaGallery, mediaGalleryGrid);
  });

  // Also handle standalone grid galleries
  mediaGalleryGrids.forEach((mediaGalleryGrid) => {
    const blockId = mediaGalleryGrid.getAttribute("data-media-gallery-block-id");
    const mediaGallery = document.querySelector(`media-gallery[data-media-gallery-block-id="${blockId}"]`);
    if (!mediaGallery) {
      triggerAnimation(null, mediaGalleryGrid);
    }
  });
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #onVariantChanged = (event) => this.variantChanged(event);

  connectedCallback() {
    super.connectedCallback();
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll("input") ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === "true"  || radio.checked);
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener("change", this.#onVariantChanged, { capture: true });
  }

  /**
   * Handles the variant change event.
   * Capture + stopPropagation keeps GTM document.onchange off the click path (if GTM is bubbling).
   * Checked state is applied sync so the selected pill can paint first.
   * Fetch / morph / URL run after the next frame via requestYieldCallback.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    event.stopPropagation();

    if (!(event.target instanceof HTMLElement)) return;

    if (this.closest("quick-add-dialog")) return;

    // Ignore custom linked-product radios; they are handled separately.
    if (event.target.matches?.("[data-linked-main-variant-input]")) return;

    const selectedOption = event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    // Sync first: checked state + pill CSS must paint before fetch/morph work.
   setTimeout(()=>{
     this.updateSelectedOption(event.target);

   },0)
    const target = event.target;
    requestYieldCallback(() => {
      this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? "" }));

      const isOnProductPage = this.dataset.templateProductMatch === "true" && !target.closest("product-card") && !target.closest("quick-add-dialog");

      // Morph the entire main content for combined listings child products, because changing the product
      // might also change other sections depending on recommendations, metafields, etc.
      const currentUrl = this.dataset.productUrl?.split("?")[0];
      const newUrl = selectedOption.dataset.connectedProductUrl;
      const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

      const url = new URL(window.location.href);

      const variantId = selectedOption.dataset.variantId || null;

      if (isOnProductPage) {
        if (variantId) {
          url.searchParams.set("variant", variantId);
        } else {
          url.searchParams.delete("variant");
        }
      }

      // Change the path if the option is connected to another product via combined listing.
      if (loadsNewProduct) {
        url.pathname = newUrl;
      }

      if (url.href !== window.location.href) {
        history.replaceState({}, "", url.toString());
      }
    });
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;

      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || "");
      const inputIndex = Number.parseInt(target.dataset.inputIndex || "");

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (!this.#checkedIndices[fieldsetIndex]) {
          const priorIndex = radios?.findIndex(
            (radio) => radio.dataset.currentChecked === "true" || radio.checked
          );
          this.#checkedIndices[fieldsetIndex] = priorIndex !== -1 ? [priorIndex] : [];
        }

        const checkedIndices = this.#checkedIndices[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = "false";
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = "false";
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = "true";
            fieldset.style.setProperty("--pill-width-current", `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`);
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = "true";
            radios[newPreviousIndex].dataset.currentChecked = "false";
            fieldset.style.setProperty("--pill-width-previous", `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`);
          }
        }
      }
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest("quick-add-component") || this.closest("swatches-variant-picker-component")) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join("&")}`;
    }
    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
          requestYieldCallback(() => {
            triggerMediaGalleryStaggeredAnimation();
          });
        } else {
          const newProduct = this.updateVariantPicker(html);

          // Update product title tag if it exists
          this.updateProductTitleTag(html);

          // Update product title share if it exists
          this.updateProductTitleShare(html);

          // Update preview book PDF if it exists
          this.updatePreviewBookPdf(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? "",
                newProduct,
              })
            );
          }

          requestYieldCallback(() => {
            triggerMediaGalleryStaggeredAnimation();
          });
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Updates the product title tag based on variant metafields.
   * @param {Document} newHtml - The new HTML containing updated title tag.
   */
  updateProductTitleTag(newHtml) {
    const productId = this.dataset.productId;
    if (!productId) return;

    // Find the current title tag element on the page
    const currentTitleTag = /** @type {HTMLElement | null} */ (document.querySelector(`[data-product-title-tag][data-product-id="${productId}"]`));
    if (!currentTitleTag) return;

    // Find the updated title tag element in the fetched HTML
    const newTitleTag = newHtml.querySelector(`[data-product-title-tag][data-product-id="${productId}"]`);

    if (newTitleTag) {
      const newTagText = newTitleTag.querySelector(".product-title-tag-text");
      const currentTagText = currentTitleTag.querySelector(".product-title-tag-text");

      if (newTagText && currentTagText) {
        const newText = newTagText.textContent?.trim() || "";

        // Hide the component if the metafield is blank
        if (newText === "") {
          currentTitleTag.style.display = "none";
        } else {
          currentTitleTag.style.display = "";
          if (newText !== currentTagText.textContent) {
            currentTagText.textContent = newText;
          }
        }
      }
    } else {
      // If no title tag in new HTML, hide the current one (metafield is blank)
      currentTitleTag.style.display = "none";
    }
  }

  /**
   * Updates the product title share based on variant metafields.
   * Also updates the Quick Buy visible title (`.view-product-title`).
   * @param {Document} newHtml - The new HTML containing updated title share.
   */
  updateProductTitleShare(newHtml) {
    const productId = this.dataset.productId;
    if (!productId) return;

    const root = this.closest("quick-add-dialog, dialog, .shopify-section") || document;

    // Find the current title share element on the page / modal
    const currentTitleShare = /** @type {HTMLElement | null} */ (
      root.querySelector(`[data-product-title-share][data-product-id="${productId}"]`) ||
        root.querySelector(`[data-product-title-share]`)
    );

    // Find the updated title share element in the fetched HTML
    const newTitleShare = newHtml.querySelector(`[data-product-title-share][data-product-id="${productId}"]`) ||
      newHtml.querySelector(`[data-product-title-share]`);

    let newText = "";
    if (newTitleShare) {
      const newTitleElement = newTitleShare.querySelector(".product-title-share-title");
      newText = newTitleElement?.textContent?.trim() || "";

      if (currentTitleShare) {
        const currentTitleElement = currentTitleShare.querySelector(".product-title-share-title");
        if (newTitleElement && currentTitleElement && newText !== currentTitleElement.textContent) {
          currentTitleElement.textContent = newText;
        }
      }
    }

    // Quick Buy modal shows `.view-product-title` (not product-title-share)
    const newViewTitle = newHtml.querySelector(".view-product-title a");
    const viewTitleText = newViewTitle?.textContent?.trim() || newText;
    const viewTitleHref = newViewTitle instanceof HTMLAnchorElement ? newViewTitle.getAttribute("href") || "" : "";

    root.querySelectorAll(".view-product-title a, .product-header a").forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (viewTitleText) node.textContent = viewTitleText;
      if (node instanceof HTMLAnchorElement && viewTitleHref) node.href = viewTitleHref;
    });
  }

  /**
   * Updates the preview book PDF based on variant metafields.
   * @param {Document} newHtml - The new HTML containing updated preview book PDF.
   */
  updatePreviewBookPdf(newHtml) {
    const productId = this.dataset.productId;
    if (!productId) return;

    // Find all preview book buttons and iframes for this product
    const currentButtons = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll(`[data-preview-book-button][data-product-id="${productId}"]`));
    const currentIframes = /** @type {NodeListOf<HTMLIFrameElement>} */ (document.querySelectorAll(`[data-preview-book-iframe][data-product-id="${productId}"]`));

    if (currentButtons.length === 0 && currentIframes.length === 0) return;

    // Find the updated preview book elements in the fetched HTML
    const newButtons = newHtml.querySelectorAll(`[data-preview-book-button][data-product-id="${productId}"]`);
    const newIframes = newHtml.querySelectorAll(`[data-preview-book-iframe][data-product-id="${productId}"]`);

    // Update iframes
    if (newIframes.length > 0 && currentIframes.length > 0) {
      const newIframe = /** @type {HTMLIFrameElement} */ (newIframes[0]);
      const newPdfUrl = newIframe.src.split("#")[0]; // Get URL without hash params

      currentIframes.forEach((currentIframe) => {
        if (newPdfUrl && newPdfUrl !== currentIframe.src.split("#")[0]) {
          currentIframe.src = newIframe.src;
        }
      });
    }

    // Update buttons visibility based on iframe src
    const firstNewIframe = /** @type {HTMLIFrameElement | null} */ (newIframes[0] || null);
    const hasPdf = firstNewIframe && firstNewIframe.src && firstNewIframe.src.trim() !== "";

    currentButtons.forEach((currentButton) => {
      if (hasPdf) {
        currentButton.style.display = "";
      } else {
        currentButton.style.display = "none";
      }
    });
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    // Only native Shopify option inputs (must have data-option-value-id).
    const selectedOption = this.querySelector(
      "select option[selected][data-option-value-id], fieldset input:checked[data-option-value-id]"
    );

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll(
        "select option[selected][data-option-value-id], fieldset input:checked[data-option-value-id]"
      )
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
